import { memo } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useValidationSchema } from 'hooks/useValidationSchema';
import { FormikTextField } from 'components/FormikTextField';
import { FormikDateField } from 'components/FormikDateField';
import { FormikPhoneInput } from 'components/PhoneInput';
import { getValidationSchema } from './validators';
import { messages } from '../../messages';
import { MemberDetailsForm } from '../../types';

interface Props {
  onBack: (values: MemberDetailsForm) => void;
  onSubmit: (values: MemberDetailsForm) => void;
  stepForm: MemberDetailsForm;
  registryName: string;
}

export const MemberDetailsStep = memo(
  ({ onBack, onSubmit, stepForm, registryName }: Props) => {
    const { t } = useTranslation();
    const validationSchema = useValidationSchema(getValidationSchema);

    return (
      <Formik
        initialValues={stepForm}
        onSubmit={values => onSubmit(values)}
        validationSchema={validationSchema}
        validateOnChange={false}
      >
        {({ errors, values }) => (
          <Form data-cy="member-details-step">
            <Stack spacing="1.6rem" direction="column" justifyContent="center">
              <Typography
                variant="body4"
                textAlign="center"
                color="button.primary"
              >
                {registryName}
              </Typography>
              <Typography variant="h1" textAlign="center">
                {t(messages.memberDetails())}
              </Typography>
              <Typography variant="body1">
                {t(messages.tellUsSomeDetails())}
              </Typography>
              <FormikTextField
                name="firstName"
                placeholder={t(messages.enterYourFirstName())}
                validationSchema={validationSchema}
                oneWayValidationOnChange={true}
                data-cy="first-name-input"
                label={t(messages.firstName())}
              />
              <FormikTextField
                name="lastName"
                placeholder={t(messages.enterYourLastName())}
                validationSchema={validationSchema}
                oneWayValidationOnChange={true}
                data-cy="last-name-input"
                label={t(messages.lastName())}
              />
              <FormikDateField
                name="dateOfBirth"
                validationSchema={validationSchema}
                oneWayValidationOnChange={true}
                data-cy="date-of-birth-input"
                disableFuture
                label={t(messages.dateOfBirth())}
              />
              <FormikPhoneInput
                name="phone"
                placeholder={t(messages.phoneNumber())}
                validationSchema={validationSchema}
                oneWayValidationOnChange={true}
                data-cy="phone-number-input"
                label={`${t(messages.phoneNumber())} (${t(
                  messages.optional(),
                )})`}
              />
              <Button
                type="submit"
                color="primary"
                variant="contained"
                data-cy="caregiver-contact-details-step-continue-button"
                style={{ marginTop: '3.2rem' }}
                disabled={Object.values(errors).length > 0}
              >
                {t(messages.continue())}
              </Button>
              <Button
                onClick={() => onBack(values)}
                color="primary"
                variant="text"
                data-cy="caregiver-contact-details-step-back-button"
                style={{ marginTop: '3.2rem' }}
              >
                {t(messages.back())}
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    );
  },
);
